import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign, CheckCircle, AlertCircle } from "lucide-react";
import { HoldingWithInstrumentAndAccount } from "@/types";

interface NAVSummarySectionProps {
  holdings: HoldingWithInstrumentAndAccount[];
}

export const NAVSummarySection: React.FC<NAVSummarySectionProps> = ({
  holdings,
}) => {
  const formatCurrency = (amount: number, currency: string = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat("en-US").format(value);
  };

  // Calculate portfolio totals by currency
  const portfolioTotals = holdings.reduce((totals, holding) => {
    const currency = holding.currency || "USD";
    if (!totals[currency]) {
      totals[currency] = 0;
    }
    totals[currency] += holding.baseAmount;
    return totals;
  }, {} as Record<string, number>);

  // Calculate confirmed vs unconfirmed holdings
  const confirmedHoldings = holdings.filter(h => h.isConfirmed);
  const unconfirmedHoldings = holdings.filter(h => !h.isConfirmed);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <DollarSign className="h-5 w-5 mr-2" />
          NAV Summary
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(portfolioTotals).map(([currency, total]) => (
            <div key={currency} className="p-4 border rounded-lg">
              <p className="text-sm text-muted-foreground">Total NAV ({currency})</p>
              <p className="text-2xl font-bold">
                {formatCurrency(total, currency)}
              </p>
            </div>
          ))}
          <div className="p-4 border rounded-lg">
            <p className="text-sm text-muted-foreground">Total Holdings</p>
            <p className="text-2xl font-bold">{formatNumber(holdings.length)}</p>
          </div>
          <div className="p-4 border rounded-lg">
            <p className="text-sm text-muted-foreground">Confirmed Holdings</p>
            <div className="flex items-center">
              <p className="text-2xl font-bold text-green-600">{formatNumber(confirmedHoldings.length)}</p>
              <CheckCircle className="h-4 w-4 ml-1 text-green-600" />
            </div>
          </div>
          <div className="p-4 border rounded-lg">
            <p className="text-sm text-muted-foreground">Unconfirmed Holdings</p>
            <div className="flex items-center">
              <p className="text-2xl font-bold text-orange-600">{formatNumber(unconfirmedHoldings.length)}</p>
              <AlertCircle className="h-4 w-4 ml-1 text-orange-600" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}; 